// src/scenes/MultiplayerGameScene.js
// v3 - Corrected inheritance, uses BaseGameScene, handles MP logic.

import BaseGameScene from "./BaseGameScene.js";
import { updatePlayerState as updatePlayerStateREST, addGameEvent, pollGameUpdates, leaveGame as leaveGameREST } from '../firebaseRestApi.js';
import { saveScoreToFirestore } from "../services/authService.js";
// No showGameOverUI needed, uses MultiplayerGameOverScene
import { auth, db, realtimeDB, firebase, isDatabaseConnected, forceReconnect } from "../firebaseConfig.js";
import MultiplayerDashboard from "../ui/MultiplayerDashboard.js";
import { hasSpecialAccess, getSpecialAccessUserId, getSpecialAccessDisplayName } from '../services/specialAccessService.js';
import sceneManager from "../utils/SceneManager.js";
import Phaser from 'phaser';

// Constants for Depths (ensure consistency with BaseGameScene)
const DEPTHS = {
    BACKGROUND: -10, GHOST_SHIPS: 5, GAME_OBJECTS: 10, EFFECTS: 60,
    TRAILS: 45, PLAYER: 50, BASE_UI: 950, DASHBOARD: 900,
    FLOATING_NOTIFICATIONS: 1000, OVERLAYS: 5000,
    
};

const SCORE_ASTEROID_TRAIL = 100 * 10;
const SCORE_GREEN_ASTEROID_TRAIL = 500 * 10;
const SCORE_SPECIAL_ASTEROID_TRAIL = 1000 * 10;
const SCORE_HYPERSPACE_ASTEROID_TRAIL = 200 * 10;
const SCORE_HYPERSPACE_GREEN_TRAIL = 300 * 10;
const SCORE_HYPERSPACE_BOX_HIT = 500 * 10;
const SCORE_BRICK_BREAK = 100 * 10;

class MultiplayerGameScene extends BaseGameScene {
    constructor() {
        // --- CORRECT KEY ---
        super({ key: "MultiplayerGameScene" });
        // --- END CORRECTION ---

        // Define score constants at the top of MultiplayerGameScene.js

      // Initialize Multiplayer-Specific Properties
      this.playerDisplayName = "Player";
      this.processedEvents = new Set();
      this.shownNotifications = new Set();
      this.gameCode = "";
      this.isHost = false;
      this.gameMode = "suddenDeath";
      this.timeLimit = 300;
      this.gameData = null;
      this.otherPlayers = {}; // Map to store ghost ship objects { playerId: ghostSpriteContainer }
      this.playerColorMap = new Map();
      this.ghostContainer = null;
      this.unsubscribe = null;
      this.lastStateSendTime = 0;
      this.updateInterval = 150; // ms
      this.connectionLost = false;
      this.reconnectAttempts = 0;
      this.maxReconnectAttempts = 5;
      this.mpDashboard = null;
      this.dashboardCreated = false;
      this.gameOverProcessed = false;
      this.playerStateTimer = null;
      this.connectionCheckTimer = null; // Timer for connection checks
      this.teamName = "Team_MP";
      this.gameStartTime = 0; // For time attack mode
      this.gameTimeRemaining = 0; // For time attack mode

      // Debug flags
      this.DEBUG_GHOST_SHIPS = true; // More detailed logging for ghosts
      this.DEBUG_EVENTS = true;     // More detailed logging for events
  }

    // --- Scene Lifecycle Methods ---

    async init(data) {
        super.init(); // Call BaseGameScene init FIRST
        console.log(`[${this.scene.key}] Initializing... Data received:`, data);

        // Reset multiplayer specific state
        this.processedEvents.clear();
        this.shownNotifications.clear();
        this.otherPlayers = {};
        this.playerColorMap.clear();
        this.gameData = null;
        this.connectionLost = false;
        this.reconnectAttempts = 0;
        this.dashboardCreated = false;
        this.gameOverProcessed = false;
        this.lastStateSendTime = 0;
        if (this.playerStateTimer) { try {this.playerStateTimer.remove();} catch(e){} this.playerStateTimer = null;}
        if (this.connectionCheckTimer) { try {this.connectionCheckTimer.remove();} catch(e){} this.connectionCheckTimer = null;}


        // Assign properties from data
        this.gameCode = data.gameCode || "";
        this.isHost = data.isHost || false;
        this.gameMode = data.gameMode || "suddenDeath";
        this.timeLimit = data.timeLimit || 300;
        this.teamName = data.teamName || `Team_${this.gameCode ? this.gameCode.substring(0,3) : 'XYZ'}`;

        // Determine Player Display Name (Handles Auth User, Guest+, Fallback)
        const user = auth.currentUser;
        const specialAccess = hasSpecialAccess();
        if (user) {
            try {
                const profileDoc = await db.collection("userProfiles").doc(user.uid).get();
                this.playerDisplayName = (profileDoc.exists && profileDoc.data().displayName)
                    ? profileDoc.data().displayName : (user.displayName || user.email || "Player");
            } catch (error) {
                console.warn("MP Init: Error fetching profile, using fallback.", error);
                this.playerDisplayName = user.displayName || user.email || "Player";
            }
        } else if (specialAccess) {
            this.playerDisplayName = getSpecialAccessDisplayName();
        } else {
            this.playerDisplayName = localStorage.getItem("tempSimpsonsName") || `Guest_${Math.floor(Math.random()*1000)}`;
            if(!localStorage.getItem("tempSimpsonsName")) localStorage.setItem("tempSimpsonsName", this.playerDisplayName);
        }
        console.log(`[${this.scene.key}] Player Display Name set to: ${this.playerDisplayName}`);

        // Reset base game properties that might persist incorrectly
        this.score = 0;
        this.lives = 3;
        this.isAlive = true;
        this.gameOver = false;
    }

    create() {
        console.log(`[${this.scene.key}] Create START`);
        try {
            // --- 1. Call Base Create FIRST ---
            super.create();
            console.log(`[${this.scene.key}] BaseGameScene create() finished.`);

            if(this.physics?.world?.isPaused) this.physics.resume();

            // --- 2. Multiplayer Specific Setup ---
            this.ghostContainer = this.add.container(0, 0).setDepth(DEPTHS.GHOST_SHIPS);
            console.log(`[${this.scene.key}] Ghost container created.`);

            if (this.player) this.player.setDepth(DEPTHS.PLAYER);

            this.createAndInitDashboard();

            if (this.gameMode === "timeAttack") {
                this.gameStartTime = this.time.now; // Use Phaser time for consistency
                this.gameTimeRemaining = this.timeLimit * 1000;
                this.time.addEvent({
                    delay: 1000,
                    callback: this.updateGameTimerDisplay,
                    callbackScope: this,
                    loop: true
                });
                console.log(`[${this.scene.key}] Time attack timer started (${this.timeLimit}s).`);
            }

            // --- 3. Initialize Networking ---
            this.initMultiplayer();

            // --- 4. Create/Override UI & Positioning ---
            this.createUI(); // Re-call to ensure layering
            this.addConnectionMonitor();
            this.handleResize();

            console.log(`[${this.scene.key}] Create FINISHED`);
        } catch (error) {
            console.error(`[${this.scene.key}] CRITICAL ERROR during create:`, error);
            this.showMessage("Game failed to load. Returning to menu.", 5000, true);
            this.time.delayedCall(5000, () => sceneManager.switchToScene("MainMenuScene"));
        }
    }

    update(time, delta) {
        try { super.update(time, delta); }
        catch (error) { console.error("Error in BaseGameScene update:", error); }

        if (this.gameOverProcessed) return; // Skip MP updates if game over is done

        // checkGameOverConditions is called in super.update()
        if (this.gameOver) return; // Exit if base check triggered game over

        // Multiplayer specific updates
        try {
            this.updateGhostShips();
            this.sendPlayerState(); // Throttling inside
        } catch (error) { console.error(`[${this.scene.key}] Error in multiplayer update loop:`, error); }
    }

    // --- UI Creation and Updates ---

    createUI() {
        super.createUI();
        console.log(`[${this.scene.key}] Base UI elements created via super.createUI().`);
        if (this.uiPanel) this.uiPanel.setDepth(DEPTHS.BASE_UI);
        if (this.statsTitle) this.statsTitle.setDepth(DEPTHS.BASE_UI + 1);
        if (this.livesText) this.livesText.setDepth(DEPTHS.BASE_UI + 1);
        if (this.scoreText) this.scoreText.setDepth(DEPTHS.BASE_UI + 1);
        if (this.shortcutText) this.shortcutText.setDepth(DEPTHS.BASE_UI + 1);
        if (this.mpDashboard?.container) this.mpDashboard.container.setDepth(DEPTHS.DASHBOARD);
        else console.warn(`[${this.scene.key}] MP Dashboard container not ready for depth setting during createUI.`);
        this.handleResize();
    }

    createAndInitDashboard() {
        if (this.mpDashboard) { console.log("MP Dashboard already exists."); this.mpDashboard.updatePosition(); return; }
        console.log("Creating and initializing MultiplayerDashboard...");
        this.mpDashboard = new MultiplayerDashboard(this);
        this.dashboardCreated = this.mpDashboard.create();
        if (this.dashboardCreated) {
            console.log("Multiplayer Dashboard created successfully.");
            if (this.mpDashboard.container) this.mpDashboard.container.setDepth(DEPTHS.DASHBOARD);
            if (this.gameData?.players) {
                const user = auth.currentUser; const specialAccess = hasSpecialAccess();
                let currentUserId = user ? user.uid : (specialAccess ? getSpecialAccessUserId() : null);
                this.mpDashboard.updatePlayerList(this.gameData.players, currentUserId);
            }
        } else { console.error("Failed to create MultiplayerDashboard."); }
    }

    addConnectionMonitor() { /* ... Implementation from BaseGameScene ... */
        this.connectionStatus = this.add.container(10, 10).setDepth(1000).setScrollFactor(0);
        const statusBg = this.add.rectangle(0, 0, 140, 28, 0x000000, 0.7).setOrigin(0, 0);
        this.connectionStatusText = this.add.text(10, 5, "✓ Connected", { fontFamily: 'monospace', fontSize: '14px', color: '#00FF00', padding: { x: 5, y: 2 } });
        this.connectionIcon = this.add.circle(130, 14, 6, 0x00FF00);
        this.connectionStatus.add([statusBg, this.connectionStatusText, this.connectionIcon]);
        this.time.delayedCall(3000, () => { if (!this.connectionLost) this.connectionStatus?.setVisible(false); });
        this.reconnectButton = this.add.container(10, 45).setDepth(1000).setScrollFactor(0).setVisible(false);
        const reconnectBg = this.add.rectangle(0, 0, 140, 28, 0xFF3300, 1).setOrigin(0, 0);
        const reconnectText = this.add.text(70, 14, "RECONNECT", { fontFamily: 'monospace', fontSize: '14px', color: '#FFFFFF', padding: { x: 5, y: 2 } }).setOrigin(0.5);
        this.reconnectButton.add([reconnectBg, reconnectText]);
        reconnectBg.setInteractive({ useHandCursor: true })
          .on('pointerdown', this.attemptReconnect.bind(this))
          .on('pointerover', () => reconnectBg.fillColor = 0xCC2200)
          .on('pointerout', () => reconnectBg.fillColor = 0xFF3300);
        this.tweens?.add({ targets: this.connectionIcon, alpha: 0.5, duration: 1000, yoyo: true, repeat: -1 });
        if (this.connectionCheckTimer) this.connectionCheckTimer.remove();
        this.connectionCheckTimer = this.time.addEvent({ delay: 10000, callback: this.checkConnection, callbackScope: this, loop: true });
    }
    async checkConnection() { /* ... Implementation from BaseGameScene ... */
        if (this.gameOver || this.gameOverProcessed) return;
        try {
            const connected = await isDatabaseConnected();
            if (!connected) {
                if (!this.connectionLost) {
                    this.connectionLost = true;
                    this.connectionStatusText?.setText("✗ Disconnected").setColor('#FF0000');
                    this.connectionIcon?.setFillStyle(0xFF0000);
                    this.connectionStatus?.setVisible(true);
                    this.reconnectButton?.setVisible(true);
                    this.addGameEvent("Connection lost. Reconnecting...", "#FF0000", { showFloating: true });
                    this.attemptReconnect(); // Start automatic attempt
                }
            } else if (this.connectionLost) {
                this.connectionLost = false;
                this.reconnectAttempts = 0;
                this.connectionStatusText?.setText("✓ Connected").setColor('#00FF00');
                this.connectionIcon?.setFillStyle(0x00FF00);
                this.reconnectButton?.setVisible(false);
                this.addGameEvent("Connection restored!", "#00FF00", { showFloating: true });
                this.time.delayedCall(3000, () => { if (!this.connectionLost) this.connectionStatus?.setVisible(false); });
            }
        } catch (error) { console.error("Error checking connection:", error); }
    }
    async attemptReconnect() { /* ... Implementation from BaseGameScene ... */
        if (this.gameOver || this.gameOverProcessed) return;
        if (this.reconnectAttempts >= this.maxReconnectAttempts) { this.addGameEvent("Max reconnect attempts reached.", "#FF0000", { showFloating: true }); return; }
        this.reconnectAttempts++;
        this.connectionStatusText?.setText("Reconnecting...").setColor('#FFFF00');
        this.connectionIcon?.setFillStyle(0xFFFF00);
        this.reconnectButton?.setVisible(false);
        try {
            await forceReconnect();
            const isConnected = await isDatabaseConnected();
            if (isConnected) {
                this.initMultiplayer(); // Re-init listener/poller
                this.checkConnection(); // Update UI immediately
            } else {
                this.connectionStatusText?.setText("Reconnect Failed").setColor('#FF0000');
                this.connectionIcon?.setFillStyle(0xFF0000);
                this.reconnectButton?.setVisible(true);
                this.addGameEvent(`Reconnect failed (${this.reconnectAttempts}/${this.maxReconnectAttempts}).`, "#FF0000", { showFloating: true });
            }
        } catch (error) {
            console.error("Error reconnecting:", error);
            this.connectionStatusText?.setText("Reconnect Error").setColor('#FF0000');
            this.connectionIcon?.setFillStyle(0xFF0000);
            this.reconnectButton?.setVisible(true);
            this.addGameEvent("Error reconnecting.", "#FF0000", { showFloating: true });
        }
    }
    handleNullGameData() { /* ... Implementation from BaseGameScene ... */
        if (!this.connectionLost && !this.gameOverProcessed) {
            this.connectionStatus?.setVisible(true);
            this.connectionStatusText?.setText("Game Not Found").setColor('#FF0000');
            this.connectionIcon?.setFillStyle(0xFF0000);
            this.addGameEvent("Game no longer exists. Returning...", "#FF0000", { showFloating: true });
            this.time.delayedCall(3000, () => this.endGame("gameNotFound"));
        }
    }
    handleConnectionError(errorCode, errorMessage) { /* ... Implementation from BaseGameScene ... */
        console.error(`Connection error: ${errorCode} - ${errorMessage}`);
        if (errorCode === "PERMISSION_DENIED") {
            this.addGameEvent("Auth error. Sign in again.", "#FF0000", { showFloating: true });
            this.time.delayedCall(3000, () => this.endGame("connectionError"));
        } else {
            this.connectionLost = true;
            this.connectionStatus?.setVisible(true);
            this.connectionStatusText?.setText("Connection Error").setColor('#FF0000');
            this.connectionIcon?.setFillStyle(0xFF0000);
            this.reconnectButton?.setVisible(true);
            this.addGameEvent(errorMessage || "Connection error.", "#FF0000", { showFloating: true });
            this.attemptReconnect();
        }
    }

    // --- Multiplayer Logic ---

    initMultiplayer() { /* ... Implementation using pollGameUpdates ... */
        if (this.unsubscribe) { try { this.unsubscribe(); } catch(e){} this.unsubscribe = null; }
        console.log(`[${this.scene.key}] Initializing multiplayer poller for game: ${this.gameCode}`);
        try {
            this.unsubscribe = pollGameUpdates(this.gameCode, (newData) => {
                if (!this || !this.scene?.systems?.scenePlugin?.isActive(this.scene.key)) { if (this.unsubscribe) { this.unsubscribe(); this.unsubscribe = null; } return; }
                if (this.gameOver || this.gameOverProcessed) return;
                if (!newData) { this.handleNullGameData(); return; }
                if (newData.error) { this.handleConnectionError(newData.error, newData.message); return; }

                const oldData = this.gameData;
                this.gameData = newData;

                if (oldData) this.checkPlayerChanges(oldData, newData);
                this.processGameEvents(newData.events);
                this.updateMultiplayerUI();

                if (newData.status === "ended" && !this.gameOver) {
                    console.log(`[MP Poller] Received 'ended' status. Triggering game over. Reason: ${newData.endReason || 'N/A'}`);
                    this.triggerGameOver(newData.endReason || "remoteEnd");
                }
            }, 1200 + Math.random() * 300);

            this.sendPlayerState(); // Initial state send

            if (this.playerStateTimer) this.playerStateTimer.remove();
            this.playerStateTimer = this.time.addEvent({ delay: this.updateInterval, callback: this.sendPlayerState, callbackScope: this, loop: true });

        } catch (error) {
            console.error(`[${this.scene.key}] Error initializing multiplayer poller:`, error);
            this.showMessage("Error connecting to game updates.", 3000, true);
        }
    }

    sendPlayerState() { /* ... Implementation using updatePlayerStateREST ... */
        const user = auth.currentUser; const specialAccess = hasSpecialAccess(); let finalUserId = null; let finalName = "Player";
        if (user) { finalUserId = user.uid; finalName = this.playerDisplayName; } else if (specialAccess) { finalUserId = getSpecialAccessUserId(); finalName = getSpecialAccessDisplayName(); }
        if (this.gameOver || this.gameOverProcessed || !finalUserId || !this.player || !this.player.active) return;
        const now = this.time.now; if (now < (this.lastStateSendTime || 0) + this.updateInterval) return; this.lastStateSendTime = now;
        const stateData = { position: { x: Math.round(this.player.x), y: Math.round(this.player.y) }, score: this.score, lives: this.lives, isAlive: this.isAlive, status: this.hyperspaceMode ? "hyperspace" : (this.isAlive ? "alive" : "dead"), name: finalName, isGuestPlus: specialAccess && !user };
        // if (this.DEBUG_GHOST_SHIPS) console.log(`Sending state for ${finalUserId}: Pos(${stateData.position.x}, ${stateData.position.y}), Score:${stateData.score}`);
        updatePlayerStateREST(this.gameCode, finalUserId, stateData).then(success => { if (!success) console.warn(`REST updatePlayerState failed for ${finalUserId}`); }).catch(err => { console.error(`REST Error updating player state for ${finalUserId}:`, err); });
    }

    updateGhostShips() { /* ... Implementation from previous response ... */
        if (this.gameOver) return; if (!this.ghostContainer || !this.ghostContainer.scene || !this.scene?.systems?.scenePlugin?.isActive(this.scene.key)) return; if (!this.gameData || !this.gameData.players) { if(this.ghostContainer.list.length > 0) try { this.ghostContainer.removeAll(true); } catch(e){} return; } if (!this.add || !this.textures || !this.textures.exists('player')) { console.error("GhostShips: Scene 'add'/'textures' not ready or 'player' texture missing!"); return; }
        const user = auth.currentUser; const specialAccess = hasSpecialAccess(); let currentUserId = user ? user.uid : (specialAccess ? getSpecialAccessUserId() : null); const playerColors = [0xFF5733, 0x33FF57, 0x3357FF, 0xF3FF33, 0x33FFF3, 0xFF33F3, 0xCCCCCC, 0xFFA500]; if (!this.playerColorMap) this.playerColorMap = new Map(); Object.keys(this.gameData.players).forEach(pId => { if (!this.playerColorMap.has(pId)) this.playerColorMap.set(pId, playerColors[this.playerColorMap.size % playerColors.length]); });
        const existingGhosts = new Map(); this.ghostContainer.list.forEach(g => { if (g.getData?.('playerId')) existingGhosts.set(g.getData('playerId'), g); }); const currentFramePlayerIds = new Set();
        Object.entries(this.gameData.players).forEach(([pId, pData]) => {
            currentFramePlayerIds.add(pId); if (pId === currentUserId || !pData || pData.isAlive === false || !pData.position || typeof pData.position.x !== 'number' || typeof pData.position.y !== 'number') { const existingGhost = existingGhosts.get(pId); if (existingGhost) { try { existingGhost.destroy(); } catch(e){} existingGhosts.delete(pId); } return; }
            const playerColor = this.playerColorMap.get(pId) || 0xAAAAAA; const finalX = pData.position.x; const finalY = pData.position.y; let ghostGroup = existingGhosts.get(pId);
            if (ghostGroup) { if (!ghostGroup.active) { try { ghostGroup.destroy(); } catch(e){} ghostGroup = null; } else { const distanceMoved = Phaser.Math.Distance.Between(ghostGroup.x, ghostGroup.y, finalX, finalY); if (distanceMoved > 3 && this.tweens) { this.tweens.add({ targets: ghostGroup, x: finalX, y: finalY, duration: this.updateInterval * 0.8, ease: 'Linear' }); } else { ghostGroup.setPosition(finalX, finalY); } const nameText = ghostGroup.list.find(el => el.type === 'Text'); if (nameText) nameText.setText(pData.name || `P:${pId.substring(0, 4)}`); } }
            if (!ghostGroup) { try { const ghostAlpha = 0.6; const ghostScale = 0.8; ghostGroup = this.add.container(finalX, finalY).setDepth(DEPTHS.GHOST_SHIPS); ghostGroup.setData('playerId', pId); const ghostSprite = this.add.sprite(0, 0, 'player').setTint(playerColor).setAlpha(ghostAlpha).setScale(ghostScale); const nameTextValue = pData.name || `P:${pId.substring(0, 4)}`; const nameText = this.add.text(0, -25 * ghostScale, nameTextValue, { fontSize: "11px", fontFamily: "monospace", color: "#FFFFFF", stroke: "#000000", strokeThickness: 2, align: 'center' }).setOrigin(0.5); ghostGroup.add([ghostSprite, nameText]); this.ghostContainer.add(ghostGroup); if(this.DEBUG_GHOST_SHIPS) console.log(`Created ghost for ${pId} at (${finalX}, ${finalY})`); ghostGroup.setAlpha(0); if(this.tweens) { this.tweens.add({ targets: ghostGroup, alpha: 1, duration: 300 }); } else { ghostGroup.setAlpha(1); } } catch(creationError) { console.error(`Error creating ghost for ${pId}:`, creationError); } }
        });
        existingGhosts.forEach((ghostGroup, pId) => { if (!currentFramePlayerIds.has(pId)) { if(this.DEBUG_GHOST_SHIPS) console.log(`Removing ghost for player ${pId}`); try { ghostGroup.destroy(); } catch(e){} } });
    }

    processGameEvents(events) { /* ... Keep implementation ... */
        if (!events || !Array.isArray(events)) return;
        events.forEach(event => {
            if (event && event.id && !this.processedEvents.has(event.id)) {
                this.processedEvents.add(event.id);
                if(this.DEBUG_EVENTS) console.log(`[MP Event] ID: ${event.id}, Msg: ${event.message}`);
                const color = event.color || "#FFFFFF";
                if (this.mpDashboard?.addEvent) this.mpDashboard.addEvent(event.message, color);
                else if (this.dashboard?.addEvent) this.dashboard.addEvent(event.message, color);
                if (event.important && typeof this.showNotification === 'function') this.showNotification(event.message, color, 3000);
            }
        });
    }

    checkPlayerChanges(oldData, newData) { /* ... Keep implementation ... */
        if (!oldData?.players || !newData?.players) return;
        const oldPlayers = oldData.players; const newPlayers = newData.players;
        Object.keys(newPlayers).forEach(pId => { if (!oldPlayers[pId]) this.addGameEvent(`${newPlayers[pId].name || 'Player'} joined!`, "#00FF00", { showFloating: true }); });
        Object.keys(oldPlayers).forEach(pId => { if (!newPlayers[pId]) this.addGameEvent(`${oldPlayers[pId].name || 'Player'} left.`, "#FF5555", { showFloating: true }); });
        Object.keys(newPlayers).forEach(pId => { if (oldPlayers[pId] && oldPlayers[pId].status !== newPlayers[pId].status) { /* Check status changes */ } });
    }

    updateMultiplayerUI() { /* ... Keep implementation ... */
        if (this.mpDashboard && this.dashboardCreated && typeof this.mpDashboard.updatePlayerList === 'function') {
            const user = auth.currentUser; const specialAccess = hasSpecialAccess(); let currentUserId = user ? user.uid : (specialAccess ? getSpecialAccessUserId() : null);
            this.mpDashboard.updatePlayerList(this.gameData?.players || {}, currentUserId);
        }
        if (this.gameMode === "timeAttack") this.updateGameTimerDisplay();
    }

    updateGameTimerDisplay() { /* ... Keep implementation ... */
        if (this.gameMode !== "timeAttack" || this.gameOver || this.gameOverProcessed) return;
        const now = this.time.now; const elapsed = now - (this.gameStartTime || now);
        this.gameTimeRemaining = Math.max(0, this.timeLimit * 1000 - elapsed);
        const minutes = Math.floor(this.gameTimeRemaining / 60000); const seconds = Math.floor((this.gameTimeRemaining % 60000) / 1000);
        const timeString = `Time: ${minutes}:${seconds.toString().padStart(2, "0")}`;
        if (this.mpDashboard?.updateTimer) this.mpDashboard.updateTimer(timeString);
        else if (this.uiElements.timerText) this.uiElements.timerText.setText(timeString);
        if (this.gameTimeRemaining <= 0 && !this.gameOver) this.triggerGameOver("timeUp");
        if (!this.gameOver && this.gameTimeRemaining <= 10000 && this.gameTimeRemaining > 9000) this.showNotification("10 seconds left!", "#FF5555", 1500);
        else if (!this.gameOver && this.gameTimeRemaining <= 30000 && this.gameTimeRemaining > 29000) this.showNotification("30 seconds left!", "#FFFF00", 1500);
    }

    // --- Overridden Base Methods ---

    onHyperspaceActivated() { /* ... Keep implementation with addGameEvent ... */
        super.onHyperspaceActivated(); const user = auth.currentUser; const specialAccess = hasSpecialAccess(); let finalUserId = user ? user.uid : (specialAccess ? getSpecialAccessUserId() : null); if (finalUserId && this.gameCode) { const playerName = this.playerDisplayName || "A player"; if(this.DEBUG_EVENTS) console.log(`Broadcasting hyperspace activation for ${playerName}`); addGameEvent(this.gameCode, `${playerName} entered hyperspace!`, "#00FFFF", true).catch(err => console.error("Error broadcasting hyperspace activation:", err)); }
    }
    onHyperspaceDeactivated() { /* ... Keep implementation with addGameEvent ... */
        super.onHyperspaceDeactivated(); const user = auth.currentUser; const specialAccess = hasSpecialAccess(); let finalUserId = user ? user.uid : (specialAccess ? getSpecialAccessUserId() : null); if (finalUserId && this.gameCode) { const playerName = this.playerDisplayName || "A player"; if(this.DEBUG_EVENTS) console.log(`Broadcasting hyperspace deactivation for ${playerName}`); addGameEvent(this.gameCode, `${playerName} exited hyperspace`, "#FFFF00", false).catch(err => console.error("Error broadcasting hyperspace deactivation:", err)); }
    }
    hitPlayer(player, asteroid) { /* ... Keep implementation with addGameEvent ... */
        const livesBeforeHit = this.lives; super.hitPlayer(player, asteroid); const livesAfterHit = this.lives; if (livesBeforeHit > livesAfterHit && !this.gameOver) { const user = auth.currentUser; const specialAccess = hasSpecialAccess(); let finalUserId = user ? user.uid : (specialAccess ? getSpecialAccessUserId() : null); if (finalUserId && this.gameCode) { const playerName = this.playerDisplayName || "A player"; const message = livesAfterHit > 0 ? `${playerName} lost a life!` : `${playerName} is out!`; const color = livesAfterHit > 0 ? "#FF5555" : "#FF0000"; const important = livesAfterHit <= 0; if(this.DEBUG_EVENTS) console.log(`Broadcasting player hit/out event for ${playerName}`); addGameEvent(this.gameCode, message, color, important).catch(err => console.error("Error broadcasting player hit/out event:", err)); } }
    }
   // Enhanced onTrailHit method for MultiplayerGameScene.js

 // Enhanced onTrailHit method for MultiplayerGameScene.js
 onTrailHit(pointsEarned, isSpecialTrigger, isHyperspaceTrailHit = false) {
    // Call the parent method first
    super.onTrailHit(pointsEarned, isSpecialTrigger, isHyperspaceTrailHit);
    
    // Make sure score display is updated
    if (this.scoreText) {
      this.scoreText.setText(`Score: ${this.score.toLocaleString()}`);
    }
    
    // Add event to multiplayer dashboard
    if (this.mpDashboard?.addEvent) {
      if (isSpecialTrigger) {
        this.mpDashboard.addEvent(`${this.playerDisplayName} triggered hyperspace!`, "#FF00FF");
        
        // Broadcast significant events to other players when special asteroids are hit
        if (this.gameCode) {
          this.addGameEvent(
            `${this.playerDisplayName} triggered hyperspace!`, 
            "#FF00FF", 
            { 
              addToLog: true, 
              showFloating: false, 
              broadcast: true // Send to other players
            }
          );
        }
      } 
      else if (isHyperspaceTrailHit) {
        this.mpDashboard.addEvent(`Hyperspace trail hit: +${pointsEarned.toLocaleString()} pts`, "#FFD700");
      } 
      else if (pointsEarned >= SCORE_GREEN_ASTEROID_TRAIL) {
        this.mpDashboard.addEvent(`Green asteroid hit: +${pointsEarned.toLocaleString()} pts`, "#00FF00");
      } 
      else {
        this.mpDashboard.addEvent(`Trail hit: +${pointsEarned.toLocaleString()} pts`, "#FFAA00");
      }
    }
    
    // Broadcast high scores in multiplayer (if host and score is significant)
    if (this.isHost && pointsEarned > 500 && this.gameCode) {
      const message = `${this.playerDisplayName} scored ${pointsEarned.toLocaleString()} points!`;
      
      this.addGameEvent(
        message, 
        "#FFAA00", 
        { 
          addToLog: true, 
          showFloating: false, 
          broadcast: true // Send to other players
        }
      );
    }
    
    // Log the event for debugging
    console.log(`[${this.scene.key}] Trail hit: ${pointsEarned} points, Special: ${isSpecialTrigger}, Hyperspace: ${isHyperspaceTrailHit}`);
  }
    hasCustomGameOver() { return true; } // We handle game over here
    onGameOver(reason = "unknown") { /* ... Keep implementation (calls endGame) ... */
        if (this.gameOverProcessed) { console.log(`[${this.scene.key}] onGameOver already processed. Reason: ${reason}`); return; }
        console.log(`[${this.scene.key}] onGameOver called. Reason: ${reason}`); this.gameOverProcessed = true; this.gameOver = true; if (this.physics?.world && !this.physics.world.isPaused) this.physics.pause(); this.endGame(reason);
    }
    // Updated endGame to ensure teamName is passed to GameOverScene
endGame(reason) {
    console.log(`[${this.scene.key}] endGame process started. Reason: ${reason}`);
    
    // Cleanup timers, listeners etc.
    this.cleanupGameResources();
    
    // Prepare game data for GameOverScene
    const finalGameDataForAI = this.prepareGameDataForAI();
    const finalPlayerScores = this.collectPlayerScores();
    
    // Get player's final score
    const user = auth.currentUser;
    const specialAccess = hasSpecialAccess();
    let currentUserId = user ? user.uid : (specialAccess ? getSpecialAccessUserId() : null);
    let finalScoreForCurrentPlayer = this.score || 0;
    
    if (currentUserId) {
        const currentPlayerFinalData = finalPlayerScores.find(p => p.userId === currentUserId);
        if (currentPlayerFinalData) finalScoreForCurrentPlayer = currentPlayerFinalData.score;
    }
    this.score = finalScoreForCurrentPlayer;
    
    // Save scores unless connection issues
    if (reason !== "connectionError" && reason !== "gameNotFound" && reason !== "deleted") {
        this.saveAllScores(finalPlayerScores, finalGameDataForAI?.teamName)
            .then(() => console.log(`[${this.scene.key}] Async score saving completed.`))
            .catch(err => console.error(`[${this.scene.key}] Async score saving failed:`, err));
    } else {
        console.log(`[${this.scene.key}] Skipping score saving due to end reason: ${reason}`);
    }
    
    // If host, mark game as ended (unless it's from remote end)
    if (this.isHost && reason !== "remoteEnd" && reason !== "gameNotFound" && reason !== "deleted") {
        this.markGameAsEndedInDB(reason)
            .catch(err => console.error(`[${this.scene.key}] Failed to mark game as ended:`, err));
    }
    
    // Prepare data to pass to GameOverScene
    const dataToPass = {
        score: this.score,
        gameData: finalGameDataForAI,
        gameCode: this.gameCode,
        playerScores: finalPlayerScores,
        gameType: "MultiplayerShortcut",
        teamName: finalGameDataForAI?.teamName || this.teamName // Explicitly pass teamName
    };
    
    // Transition to GameOverScene after a slight delay
    this.time.delayedCall(100, () => {
        console.log(`[${this.scene.key}] Executing transition to MultiplayerGameOverScene.`);
        console.log("Data being passed to GameOverScene:", dataToPass);
        
        try {
            if (this.scene?.systems?.scenePlugin?.isActive(this.scene.key)) {
                sceneManager.switchToScene('MultiplayerGameOverScene', dataToPass);
            } else {
                console.warn(`[${this.scene.key}] Scene became inactive before game over transition.`);
            }
        } catch (transitionError) {
            console.error(`[${this.scene.key}] Failed to transition to game over scene:`, transitionError);
            this.showDomGameOver();
        }
    });
}

    // --- Utilities ---
    showMessage(message, duration = 2000, isError = false) { /* ... Use BaseGameScene's showNotification ... */
        this.showNotification(message, isError ? '#FF5555' : '#FFFFFF', duration);
    }
    showNotification(message, color = "#FFFFFF", duration = 2000) { /* ... Keep implementation from BaseGameScene ... */
        if (!this.scene?.systems?.scenePlugin?.isActive(this.scene.key) || !this.add || !this.tweens) return; const notificationKey = `${message}_${Math.floor(Date.now() / 5000)}`; if (this.shownNotifications?.has(notificationKey)) return; if (!this.shownNotifications) this.shownNotifications = new Set(); this.shownNotifications.add(notificationKey); this.time?.delayedCall(5000, () => this.shownNotifications?.delete(notificationKey)); if (this.activeNotification) { this.tweens?.killTweensOf(this.activeNotification); this.activeNotification.forEach(el => el?.destroy()); this.activeNotification = null; } const width = this.cameras.main.width; const height = this.cameras.main.height; const notificationY = height * 0.15; const container = this.add.container(width / 2, notificationY - 30).setDepth(DEPTHS.FLOATING_NOTIFICATIONS).setAlpha(0); const textStyle = { fontFamily: 'monospace', fontSize: '18px', fontWeight: 'bold', color: color, stroke: '#000000', strokeThickness: 4, backgroundColor: '#00000099', padding: { x: 15, y: 8 }, align: 'center' }; const msgText = this.add.text(0, 0, message, textStyle).setOrigin(0.5); container.add(msgText); this.activeNotification = [container, msgText]; this.tweens.add({ targets: container, y: notificationY, alpha: 1, scale: { from: 0.8, to: 1 }, duration: 300, ease: 'Back.easeOut', onComplete: () => { if (!container.active) return; this.time.delayedCall(duration, () => { if (!container.active) return; this.tweens.add({ targets: container, y: notificationY - 30, alpha: 0, scale: 0.8, duration: 300, ease: 'Cubic.easeIn', onComplete: () => { if (container.active) container.destroy(); if (this.activeNotification && this.activeNotification[0] === container) this.activeNotification = null; } }); }); } }); return container;
    }
    addGameEvent(message, color = "#FFFFFF", options = {}) { /* ... Keep implementation ... */
        const defaultOptions = { addToLog: true, showFloating: false, duration: 2000, broadcast: false }; options = { ...defaultOptions, ...options };
        if (options.addToLog && this.mpDashboard?.addEvent) this.mpDashboard.addEvent(message, color);
        else if (options.addToLog && this.dashboard?.addEvent) this.dashboard.addEvent(message, color); // Fallback
        if (options.showFloating) this.showNotification(message, color, options.duration);
        if (options.broadcast && this.isHost) addGameEvent(this.gameCode, message, color, options.showFloating).catch(err => console.error("Error broadcasting game event:", err));
    }
    // Updated prepareGameDataForAI to include teamName
prepareGameDataForAI() {
    console.log("Preparing game data for AI...");
    const baseData = this.gameData || {};
    
    const enrichedData = {
        gameCode: this.gameCode,
        gameMode: baseData.gameMode || 'suddenDeath',
        teamName: baseData.teamName || this.teamName || `Team_${this.gameCode ? this.gameCode.substring(0,3) : 'XYZ'}`,
        startedAt: baseData.startedAt || (Date.now() - 300000),
        endedAt: Date.now(),
        playerCount: baseData.playerCount || Object.keys(baseData.players || {}).length || 1,
        stats: {}
    };

    const players = baseData.players || {};
    const playerValues = Object.values(players);
    
    if (playerValues.length > 0) {
        const duration = Math.max(0, (enrichedData.endedAt - enrichedData.startedAt) / 1000);
        enrichedData.duration = duration;
        
        let totalScore = 0;
        let maxScore = 0;
        let minScore = Infinity;
        let totalHyperspaceActivations = 0;
        
        playerValues.forEach(player => {
            const score = player.score || 0;
            totalScore += score;
            if (score > maxScore) maxScore = score;
            if (score < minScore) minScore = score;
            totalHyperspaceActivations += player.hyperspaceActivations || 0;
        });
        
        enrichedData.stats = {
            highestScore: maxScore,
            averageScore: totalScore / playerValues.length,
            hyperspaceCount: totalHyperspaceActivations
        };
        
        const currentUserId = auth?.currentUser?.uid;
        if (currentUserId && players[currentUserId]) {
            const currentPlayer = players[currentUserId];
            enrichedData.playerStats = {
                score: currentPlayer.score || 0,
                lives: currentPlayer.lives !== undefined ? currentPlayer.lives : 3,
                hyperspaceActivations: currentPlayer.hyperspaceActivations || 0,
                shortcutsUsed: currentPlayer.shortcutsUsed || 0
            };
        }
    }
    
    console.log("Enriched game data for AI:", enrichedData);
    return enrichedData;
}
    // Updated collectPlayerScores to include teamName
collectPlayerScores() {
    console.log("Collecting player scores...");
    
    if (!this.gameData || !this.gameData.players) {
        console.log("No game data/players for score collection.");
        const user = auth.currentUser;
        const specialAccess = hasSpecialAccess();
        let fbUserId = user ? user.uid : null;
        let fbName = user ? this.playerDisplayName : null;
        
        if (!fbUserId && specialAccess) {
            fbUserId = getSpecialAccessUserId();
            fbName = getSpecialAccessDisplayName();
        }
        
        const fallbackScore = {
            userId: fbUserId || 'localPlayer',
            player: fbName || 'Player',
            name: fbName || 'Player',
            score: this.score || 0,
            isAlive: false,
            rank: 1,
            teamName: this.teamName
        };
        
        console.log("Using fallback score:", fallbackScore);
        return [fallbackScore];
    }
    
    const playerScores = [];
    
    Object.entries(this.gameData.players).forEach(([playerId, playerData]) => {
        playerScores.push({
            userId: playerId,
            player: playerData.name || "Player",
            name: playerData.name || "Player",
            score: playerData.score || 0,
            isAlive: playerData.isAlive !== false,
            teamName: this.gameData.teamName || this.teamName
        });
    });
    
    playerScores.sort((a, b) => b.score - a.score);
    
    playerScores.forEach((player, index) => {
        player.rank = index + 1;
    });
    
    console.log("Collected player scores from RTDB:", playerScores);
    return playerScores;
}
    async saveAllScores(playerScores, teamName) { /* ... Keep implementation ... */
        const gameType = "MultiplayerShortcut"; const serverTimestamp = firebase.firestore.FieldValue.serverTimestamp(); const teamNameOrDefault = teamName || `Team_${this.gameCode ? this.gameCode.substring(0, 3) : '???'}`;
        const savePromises = playerScores.map(playerScore => { const isVerifiedUser = playerScore.userId && !playerScore.userId.startsWith('guestplus_') && !playerScore.userId.startsWith('guest_'); const scoreData = { score: playerScore.score, gameType: gameType, player: playerScore.player, userUID: playerScore.userId, teamName: teamNameOrDefault, timestamp: serverTimestamp, verified: isVerifiedUser, gameCode: this.gameCode }; return saveScoreToFirestore(scoreData, true).catch(err => console.error(`Score save error for ${playerScore.userId}:`, err)); });
        if (this.isHost && playerScores.length > 1 && typeof this.saveTeamScore === 'function') { savePromises.push( this.saveTeamScore(teamNameOrDefault, playerScores).catch(err => console.error("Team score save error:", err)) ); }
        await Promise.allSettled(savePromises); console.log(`[${this.scene.key}] Score saving process finished.`);
    }
    async saveTeamScore(teamName, playerScores) { /* ... Keep implementation ... */
        if (!teamName || !playerScores || playerScores.length === 0) return; try { const totalScore = playerScores.reduce((sum, player) => sum + (player.score || 0), 0); const playerNames = playerScores.map(p => p.player || "Player").join(', '); const teamScoreData = { teamName: teamName, totalScore: totalScore, playerCount: playerScores.length, playerNames: playerNames, gameCode: this.gameCode, gameMode: this.gameMode, gameType: "MultiplayerShortcut", timestamp: firebase.firestore.FieldValue.serverTimestamp() }; await db.collection("teamScores").add(teamScoreData); console.log(`Team score saved for ${teamName}: ${totalScore}`); } catch (error) { console.error("Failed to save team score:", error); }
    }
    async markGameAsEndedInDB(reason) { /* ... Keep implementation ... */
        if (!this.isHost || !this.gameCode) return; if (!realtimeDB || typeof realtimeDB.ref !== 'function') { console.error("Realtime Database not available to mark game end."); return; } try { const gameRef = realtimeDB.ref(`games/${this.gameCode}`); await gameRef.update({ status: 'ended', endedAt: firebase.database.ServerValue.TIMESTAMP, lastUpdated: firebase.database.ServerValue.TIMESTAMP, endReason: reason || 'unknown' }); console.log(`[${this.scene.key}] Game ${this.gameCode} marked as ended in RTDB.`); } catch (error) { console.error(`[${this.scene.key}] Error marking game ${this.gameCode} as ended:`, error); }
    }
    cleanupGameResources() { /* ... Keep implementation ... */
        console.log(`[${this.scene.key}] Cleaning up game resources...`); if (this.physics?.world) try { this.physics.pause(); } catch (e) {} if (this.time) try { this.time.removeAllEvents(); } catch (e) {} if (this.tweens) try { this.tweens.killAll(); } catch (e) {} if (this.unsubscribe) try { this.unsubscribe(); this.unsubscribe = null; } catch (e) {} if (this.playerStateTimer) { try {this.playerStateTimer.remove();} catch(e){} this.playerStateTimer = null;} if (this.connectionCheckTimer) { try {this.connectionCheckTimer.remove();} catch(e){} this.connectionCheckTimer = null;}
        const safeHide = (obj) => { if (obj && obj.setVisible) try { obj.setVisible(false); } catch(e){} }; safeHide(this.asteroids); safeHide(this.ghostContainer); safeHide(this.mpDashboard?.container); safeHide(this.player); safeHide(this.trails); safeHide(this.bricks); console.log(`[${this.scene.key}] Game resources cleanup finished.`);
    }
    showDomGameOver() { /* ... Keep implementation ... */
        console.log("Showing DOM-based game over screen"); try { const existingOverlay = document.getElementById('mp-game-over-overlay-dom'); if (existingOverlay) existingOverlay.remove(); const overlay = document.createElement('div'); overlay.id = 'mp-game-over-overlay-dom'; Object.assign(overlay.style, { position: 'fixed', top: '0', left: '0', width: '100%', height: '100%', backgroundColor: 'rgba(0, 0, 33, 0.9)', zIndex: '10000', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', fontFamily: 'Arial, sans-serif', color: 'white' }); overlay.innerHTML = `<div style="text-align: center; padding: 30px; background-color: rgba(0,0,50,0.8); border-radius: 10px; width: 80%; max-width: 600px;"><h1 style="color: #FF5555; font-size: 48px; margin-bottom: 20px;">GAME OVER</h1><h2 style="color: white; font-size: 28px; margin-bottom: 30px;">Your Score: ${this.score}</h2><button id="dom-menu-button" style="background-color: #00AA00; color: white; border: none; padding: 15px 30px; font-size: 24px; border-radius: 8px; cursor: pointer; margin-top: 20px;">RETURN TO MENU</button></div>`; document.body.appendChild(overlay); document.getElementById('dom-menu-button').addEventListener('click', () => { overlay.remove(); console.log("Returning to menu via DOM fallback button"); if (sceneManager) { sceneManager.switchToScene("MainMenuScene"); } else if (window.gameInstance?.scene) { window.gameInstance.scene.start("MainMenuScene"); } else { window.location.reload(); } }); } catch (error) { console.error("Error creating DOM game over:", error); alert(`Game Over! Score: ${this.score}. Error displaying results.`); window.location.reload(); }
    }
    handleResize(gameSize) { /* ... Keep implementation ... */
        super.handleResize(gameSize); if (this.mpDashboard?.updatePosition) this.mpDashboard.updatePosition(); if (this.connectionStatus) this.connectionStatus.setPosition(10, 10); if (this.reconnectButton) this.reconnectButton.setPosition(10, 45); if (this.fixButton) { this.fixButton.setPosition(this.cameras.main.width - 100, this.cameras.main.height - 30); if(this.fixText) this.fixText.setPosition(this.cameras.main.width - 100, this.cameras.main.height - 30); }
    }
    shutdown() { /* ... Keep implementation ... */
        const sceneKey = this.scene?.key || 'MultiplayerGameScene'; console.log(`[${sceneKey}] Shutting down...`); this.cleanupGameResources(); if (this.mpDashboard) { try { this.mpDashboard.destroy(); } catch(e){} this.mpDashboard = null; this.dashboardCreated = false; } if (this.ghostContainer) { try { this.ghostContainer.destroy(true); } catch(e){} this.ghostContainer = null; } this.playerColorMap.clear(); this.processedEvents.clear(); this.shownNotifications.clear(); super.shutdown(); console.log(`[${sceneKey}] Shutdown complete.`);
    }

} // End of MultiplayerGameScene class

export default MultiplayerGameScene;